<template>
  <div
    class="backbone-container"
  />
</template>

<script>
  import layout from 'legacy/layout';
  import {SET_APP_LOADING} from 'store/modules/common/common';
  import routeArgsToInt from 'router/utils/routeArgsToInt';
  import _ from 'lodash';
  import handleApiError from 'utils/api/errors/handleApiError';
  import ApiNotFoundError from 'utils/api/errors/ApiNotFoundError';
  import {show403, show404} from 'router/utils/errorPages';
  import ApiPermissionDeniedError from 'utils/api/errors/ApiPermissionDeniedError';
  import AppEventBus from 'utils/appEventBus';
  import {mapActions} from 'vuex';

  _.noConflict();

  export default {
    name: 'BackboneViewWrapper',
    data () {
      return {
        viewFunction () {
        },
        prevParamsJSON: null,
        view: null,
        viewPromise: null
      };
    },
    computed: {
      currentRoute () {
        return this.$route;
      },

      /*
       * router args may be integer or string depending on navigate method,
       * to work correctly we should convert args to same type, integer is ok,
       * because all legacy routes has only integers as route args
       */
      routeParams () {
        return routeArgsToInt(this.currentRoute);
      },
      orderedRouteParams () {
        const orderedIdsInRoute = this.currentRoute.fullPath.split('/').map(i => parseInt(i)).
          filter(i => Boolean(i));

        // this validation is to be sure that everything works fine
        if (Object.keys(this.routeParams).length !== orderedIdsInRoute.length) {
          console.error('Route params length miss match');
        }

        if (_.difference(Object.values(this.routeParams), orderedIdsInRoute).length) {
          console.error('Different route params found');
        }

        return orderedIdsInRoute;
      }
    },
    watch: {
      routeParams () {
        /*
         * do not trigger rerender if params are not changed, params object is updated on every navigation,
         * but real values may stay the same
         */
        if (this.prevParamsJSON !== JSON.stringify(this.routeParams)) {
          this.restartView();
        }
      }
    },
    beforeDestroy () {
      this.destroyView();
    },
    mounted () {
      this.$nextTick(async () => {
        this.initView();
      });
    },
    methods: {
      ...mapActions('common', {
        setAppLoading: SET_APP_LOADING
      }),
      async restartView () {
        await this.destroyView();
        this.initView();
      },
      async initView () {
        this.viewPromise = this._initView();
        try {
          await this.viewPromise;
        } catch (e) {

          // clear view promise this error will happen in destroy
          this.viewPromise = null;

          if (e instanceof ApiNotFoundError) {
            return show404();
          } else if (e instanceof ApiPermissionDeniedError) {
            return show403();
          }

          throw e;
        }
        this.viewPromise = null;
        AppEventBus.$emit('BACKBONE_WRAPPED_VIEW_RENDERED');
      },
      async _initView () {
        this.prevParamsJSON = JSON.stringify(this.routeParams);
        this.setAppLoading(true);

        // not reliable code (order of keys is not consistent)
        const routeArgs = this.orderedRouteParams;
        let view;
        try {
          view = await this.viewFunction(...routeArgs);
        } catch (e) {
          return handleApiError(e);
        }

        this.view = this.renderView(view);

        this.setAppLoading(false);
      },
      renderView (view) {
        if (!view) {
          return;
        }

        if (view.open) {
          view.open();
        }

        view.render();

        this.$el.appendChild(view.el);

        if (view.afterShow) {
          view.afterShow();
        }

        return view;
      },
      async destroyView () {
        /*
         * to prevent race conditions we should be sure that previous render is completed
         * without this check we can render content multiple times,
         * because destroy was called before render is completed -> this.view is null and nothing to delete
         * (view function is promise)
         */
        if (this.viewPromise) {
          await this.viewPromise;
        }

        layout.header.close();
        if (!this.view) {
          return;
        }
        if (this.view.close) {
          this.view.close();
        }
        this.view.remove();
        this.view = null;

        // clear child elements
        while (this.$el.firstChild){
          this.$el.removeChild(this.$el.firstChild);
        }
      }
    }
  };
</script>
